import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowsClockwise,
  ArrowSquareOut,
  CheckCircle,
  Warning,
  ArrowLeft,
  Check,
  Sparkle,
} from '@phosphor-icons/react';
import {
  apiClient,
  API_ENDPOINTS,
  MERCHANT_ROUTES,
  Button,
  Badge,
  Spinner,
  Input,
  Label,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';
import { GoogleSyncStatusPanel } from '../components/settings/GoogleSyncStatusPanel';
import { confirmAction } from '../components/modals/ConfirmModal';

function AssetIcon({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return <img src={src} alt={alt} className={className} />;
}

interface StoreSettings {
  googleAdsConversionId?: string;
  googleAdsConversionLabel?: string;
  googleSiteVerification?: string;
  [key: string]: any;
}

interface Store {
  id: string;
  name: string;
  country?: string;
  currency?: string;
  googleMerchantId: string | null;
  googleMerchantDataSourceId: string | null;
  settings: StoreSettings | null;
}

interface GoogleStatus {
  isConnected: boolean;
  linkStatus: 'pending' | 'linked' | 'error' | 'disconnected';
  googleEmail?: string;
  gmcAccountId?: string;
  gmcAccountName?: string;
  adsCustomerId?: string;
  adsAccountName?: string;
  lastSyncedAt?: string;
  lastError?: string;
  tokenExpiringSoon?: boolean;
  connectedSince?: string;
}

interface AccountOption {
  id: string;
  name: string;
}

interface AccountsResponse {
  connectedAs: string;
  gmcAccounts: AccountOption[];
  adsAccounts: AccountOption[];
}

type TabType = 'overview' | 'setup' | 'settings';

export default function GoogleCommercePage() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const { addNotification } = useNotificationStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedGmcId, setSelectedGmcId] = useState('');
  const [selectedGmcName, setSelectedGmcName] = useState('');

  const [selectedAdsId, setSelectedAdsId] = useState('');
  const [selectedAdsName, setSelectedAdsName] = useState('');

  const [conversionId, setConversionId] = useState('');
  const [conversionLabel, setConversionLabel] = useState('');
  const [siteVerification, setSiteVerification] = useState('');

  const [checkedRequirements, setCheckedRequirements] = useState({
    paymentMethod: false,
    onlineStore: false,
    refundPolicy: false,
    contactInfo: false,
  });
  const [termsAgreed, setTermsAgreed] = useState(false);

  useEffect(() => {
    const pickAccounts = searchParams.get('google_pick_accounts');
    const errorParam = searchParams.get('google_error');

    if (pickAccounts === 'true') {
      setIsPickerOpen(true);
      setActiveTab('setup');
      addNotification({
        type: 'info',
        title: 'Google Connected',
        message: 'Please select your Merchant Center and Google Ads accounts.',
      });
      setSearchParams({});
    } else if (errorParam) {
      addNotification({
        type: 'error',
        title: 'Google Connection Failed',
        message: decodeURIComponent(errorParam),
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, addNotification]);

  const { data: store, isLoading: isStoreLoading } = useQuery<Store>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: () => apiClient.get<Store>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  const { data: googleStatus, isLoading: isStatusLoading } = useQuery<GoogleStatus>({
    queryKey: ['google-status', activeStoreId],
    queryFn: () => apiClient.get<GoogleStatus>(API_ENDPOINTS.GOOGLE.STATUS),
    enabled: !!activeStoreId,
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: accounts,
    isFetching: isAccountsFetching,
    refetch: refetchAccounts,
  } = useQuery<AccountsResponse>({
    queryKey: ['google-accounts', activeStoreId],
    queryFn: () => apiClient.get<AccountsResponse>(API_ENDPOINTS.GOOGLE.ACCOUNTS),
    enabled: isPickerOpen && !!activeStoreId,
  });

  useEffect(() => {
    if (store?.settings) {
      setConversionId(store.settings.googleAdsConversionId || '');
      setConversionLabel(store.settings.googleAdsConversionLabel || '');
      setSiteVerification(store.settings.googleSiteVerification || '');

      if (store.settings.googleRequirementsVerified) {
        setCheckedRequirements({
          paymentMethod: true,
          onlineStore: true,
          refundPolicy: true,
          contactInfo: true,
        });
      }
      if (store.settings.googleTermsAgreed) {
        setTermsAgreed(true);
      }
    }
  }, [store]);

  useEffect(() => {
    if (!accounts) return;
    if (accounts?.gmcAccounts?.length === 1 && !selectedGmcId) {
      setSelectedGmcId(accounts.gmcAccounts[0].id);
      setSelectedGmcName(accounts.gmcAccounts[0].name);
    }
    if (accounts?.adsAccounts?.length === 1 && !selectedAdsId) {
      setSelectedAdsId(accounts.adsAccounts[0].id);
      setSelectedAdsName(accounts.adsAccounts[0].name);
    }
  }, [accounts, selectedGmcId, selectedAdsId]);

  const updateSettingsMutation = useMutation({
    mutationFn: (settings: Partial<StoreSettings>) =>
      apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, { settings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      addNotification({ type: 'success', title: 'Settings Saved', message: 'Google Ads tracking updated.' });
    },
    onError: (error: any) => {
      addNotification({ type: 'error', title: 'Update Failed', message: error.message });
    },
  });

  const saveOnboardingMutation = useMutation({
    mutationFn: (settings: Record<string, any>) =>
      apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, { settings }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      if (variables.googleSetupCompleted) {
        setActiveTab('overview');
        addNotification({
          type: 'success',
          title: 'Merchant Center Configured',
          message: 'Your Google channel setup is complete.',
        });
      } else if (variables.googleRequirementsVerified) {
        addNotification({
          type: 'success',
          title: 'Requirements Verified',
          message: 'Online store requirements confirmed and saved.',
        });
      }
    },
    onError: (error: any) => {
      addNotification({ type: 'error', title: 'Save Failed', message: error.message });
    },
  });

  const connectMutation = useMutation({
    mutationFn: () =>
      apiClient.post(API_ENDPOINTS.GOOGLE.CONNECT, {
        gmcAccountId: selectedGmcId || undefined,
        gmcAccountName: selectedGmcName || undefined,
        adsCustomerId: selectedAdsId || undefined,
        adsAccountName: selectedAdsName || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-status', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      setIsPickerOpen(false);
      setActiveTab('overview');
      addNotification({
        type: 'success',
        title: 'Google Connected',
        message: 'Your Google accounts have been successfully linked.',
      });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Connection Failed',
        message: error.message || 'Failed to link Google accounts.',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.GOOGLE.DISCONNECT),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-status', activeStoreId] });
      addNotification({ type: 'success', title: 'Disconnected', message: 'Google account has been unlinked.' });
    },
    onError: (error: any) => {
      addNotification({ type: 'error', title: 'Disconnect Failed', message: error.message });
    },
  });

  const handleConnectGoogle = async () => {
    try {
      const response = await apiClient.get<{ authUrl: string }>(API_ENDPOINTS.GOOGLE.AUTH_URL);
      if (response.authUrl) window.location.href = response.authUrl;
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Connection Error',
        message: error.message || 'Failed to initiate Google connection.',
      });
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await confirmAction({
      title: 'Disconnect Google',
      message: 'Disconnecting Google will revoke access tokens. Sellers can reconnect at any time. Continue?',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
    });
    if (confirmed) {
      disconnectMutation.mutate();
    }
  };

  const isLoading = isStoreLoading || isStatusLoading;
  const isConnected = googleStatus?.isConnected;
  const isGmcLinked = !!store?.googleMerchantId;
  const hasError = googleStatus?.linkStatus === 'error';

  const completedRequirementsCount = Object.values(checkedRequirements).filter(Boolean).length;
  const completedTasksCount =
    (isConnected ? 1 : 0) +
    (isGmcLinked ? 1 : 0) +
    (completedRequirementsCount === 4 ? 1 : 0) +
    1 +
    (termsAgreed ? 1 : 0);
  const totalTasksCount = 5;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80">
        <Spinner className="w-6 h-6 text-[#202223] dark:text-white" />
      </div>
    );
  }

  return (
    <div className="max-w-[1040px] mx-auto space-y-6 pb-20">
      {/* Top Navigation & Channel Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl px-6 pt-5 pb-0 shadow-2xs">
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center gap-3">
            <AssetIcon src="/icons/google-icon.svg" alt="Google" className="w-6 h-6 shrink-0" />
            <div>
              <h1 className="text-base font-bold text-[#202223] dark:text-white">Google & YouTube</h1>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Sync product catalogs to Google Merchant Center and display listings on Search & YouTube
              </p>
            </div>
          </div>
          <Badge variant={isConnected && isGmcLinked ? 'success' : 'outline'}>
            {isConnected && isGmcLinked ? 'Connected' : 'Not Connected'}
          </Badge>
        </div>

        {/* Tabs Bar */}
        <div className="flex items-center gap-6 text-xs font-semibold border-t border-[#E1E3E5] dark:border-slate-800 pt-3">
          {[
            { id: 'overview', label: 'Overview' },
            {
              id: 'setup',
              label: 'Set up Merchant Center',
              badge: isGmcLinked && store?.settings?.googleSetupCompleted ? 'Completed' : 'Required',
            },
            { id: 'settings', label: 'Settings' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`pb-3 relative flex items-center gap-1.5 transition-colors ${
                activeTab === tab.id
                  ? 'text-[#202223] dark:text-white font-bold'
                  : 'text-[#5C5F62] hover:text-[#202223] dark:hover:text-white'
              }`}
            >
              <span>{tab.label}</span>
              {tab.badge && (
                <span
                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    tab.badge === 'Completed'
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300'
                      : 'bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#202223] dark:bg-white rounded-t" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB 1: OVERVIEW ──────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {hasError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl">
              <Warning weight="fill" className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900 dark:text-red-300 text-xs">Google Action Required</h3>
                <p className="text-xs text-red-800 dark:text-red-400 mt-0.5">
                  {googleStatus?.lastError || 'Your Google access has an issue. Reconnect to resume product syncing.'}
                </p>
                <Button
                  onClick={handleConnectGoogle}
                  className="mt-2.5 h-8 px-3.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg"
                >
                  <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                  Reconnect Google Account
                </Button>
              </div>
            </div>
          )}

          {/* Shopify Polaris Informational Box */}
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-2xs flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="w-full md:w-5/12 bg-[#F6F6F7] dark:bg-slate-900 rounded-xl p-4 border border-[#E1E3E5] dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between border-b border-[#E1E3E5] dark:border-slate-800 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-xs font-bold text-[#202223] dark:text-white">Google Shopping Sync</span>
                </div>
                <span className="text-[10px] font-semibold text-[#5C5F62]">Active Feed</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-white dark:bg-slate-800 border border-[#E1E3E5] dark:border-slate-700">
                  <div className="flex items-center gap-2.5">
                      <AssetIcon src="/icons/google-icon.svg" alt="Google" className="w-5 h-5 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-[#202223] dark:text-white">Store Products</p>
                      <p className="text-[10px] text-[#5C5F62]">Google Merchant Center</p>
                    </div>
                  </div>
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="w-full md:w-7/12 space-y-3">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#F6F6F7] dark:bg-slate-800 text-[#202223] dark:text-white text-[11px] font-bold border border-[#E1E3E5] dark:border-slate-700">
                <Sparkle weight="fill" className="w-3.5 h-3.5" />
                Get your products on Google with Merchant Center
              </div>

              <p className="text-[#202223] dark:text-white text-sm font-semibold leading-relaxed">
                Easily manage and promote your business and products across Google Search, Shopping, and YouTube.
              </p>

              <div>
                <a
                  href="https://support.google.com/merchants"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-[#202223] dark:text-white hover:underline inline-flex items-center gap-1"
                >
                  Learn more about Merchant Center Next <ArrowSquareOut weight="bold" className="w-3 h-3" />
                </a>
              </div>

              <div className="pt-2">
                <Button
                  onClick={() => setActiveTab('setup')}
                  className="h-8 px-4 bg-[#202223] dark:bg-white hover:opacity-90 text-white dark:text-slate-900 font-bold text-xs rounded-lg shadow-2xs"
                >
                  {isConnected && isGmcLinked ? 'Manage setup' : 'Get started'}
                </Button>
              </div>
            </div>
          </div>

          {/* Connected services section */}
          <div className="space-y-3">
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Connected services</h3>

            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-2xs">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 border border-[#E1E3E5] dark:border-slate-700 flex items-center justify-center text-[#202223] dark:text-white">
                  <AssetIcon src="/icons/google-analytics.svg" alt="Google Analytics" className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-[#202223] dark:text-white text-xs">Google Analytics</h4>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                    Performance data can be found in the Google Analytics Console.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => window.open('https://analytics.google.com/', '_blank')}
                className="text-xs font-semibold h-8 px-3.5 rounded-lg border-[#D2D5D9]"
              >
                <ArrowSquareOut weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                View performance in Google Analytics
              </Button>
            </div>
          </div>

          {/* Active Feed Sync Status Panel */}
          {isConnected && isGmcLinked && (
            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-bold text-[#202223] dark:text-white">Real-Time Catalog Sync</h3>
              <GoogleSyncStatusPanel />
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: SETUP WIZARD ──────────────────────────────────────────── */}
      {activeTab === 'setup' && (
        <div className="space-y-6">
          <button
            onClick={() => setActiveTab('overview')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#5C5F62] hover:text-[#202223] dark:hover:text-white transition-colors"
          >
            <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
            Back to Overview
          </button>

          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 space-y-6 shadow-2xs">
            <div className="space-y-1">
              <h2 className="text-sm font-bold text-[#202223] dark:text-white">Set up your online store</h2>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 max-w-2xl">
                Complete these checklist tasks to submit your catalog to Google Merchant Center
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs font-bold text-[#202223] dark:text-white">
                <span>
                  {completedTasksCount} of {totalTasksCount} tasks completed
                </span>
                <span>{Math.round((completedTasksCount / totalTasksCount) * 100)}%</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-[#E1E3E5] dark:bg-slate-800 overflow-hidden">
                <div
                  className="h-full bg-[#202223] dark:bg-white transition-all duration-300 rounded-full"
                  style={{ width: `${(completedTasksCount / totalTasksCount) * 100}%` }}
                />
              </div>
            </div>

            <div className="space-y-4 divide-y divide-[#E1E3E5] dark:divide-slate-800 pt-2">
              {/* Step 1: Your Google account */}
              <div className="pt-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {isConnected ? (
                    <div className="w-6 h-6 rounded-full bg-[#202223] dark:bg-white text-white dark:text-slate-900 flex items-center justify-center shrink-0">
                      <Check weight="bold" className="w-3.5 h-3.5" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border border-[#D2D5D9] flex items-center justify-center text-xs font-bold text-[#5C5F62] shrink-0">
                      1
                    </div>
                  )}
                  <div>
                    <h3 className="text-xs font-bold text-[#202223] dark:text-white">Your Google account</h3>
                    <p className="text-xs text-[#5C5F62]">
                      {isConnected
                        ? `Connected as ${googleStatus?.googleEmail || 'Authorized Account'}`
                        : 'Connect your Google account to access Merchant Center'}
                    </p>
                  </div>
                </div>
                {!isConnected ? (
                  <Button onClick={handleConnectGoogle} className="h-8 px-4 text-xs font-bold">
                    Connect
                  </Button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    className="text-xs font-semibold text-[#5C5F62] hover:text-red-600"
                  >
                    Disconnect
                  </button>
                )}
              </div>

              {/* Step 2: Merchant Center account */}
              <div className="pt-4 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {isGmcLinked ? (
                      <div className="w-6 h-6 rounded-full bg-[#202223] dark:bg-white text-white dark:text-slate-900 flex items-center justify-center shrink-0">
                        <Check weight="bold" className="w-3.5 h-3.5" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border border-[#D2D5D9] flex items-center justify-center text-xs font-bold text-[#5C5F62] shrink-0">
                        2
                      </div>
                    )}
                    <div>
                      <h3 className="text-xs font-bold text-[#202223] dark:text-white">
                        Connect your Google Merchant Center account
                      </h3>
                      {isGmcLinked ? (
                        <p className="text-xs text-[#5C5F62] mt-0.5">
                          Merchant Center ID: <span className="font-mono font-semibold">{store?.googleMerchantId}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-[#5C5F62] mt-0.5">
                          Select a Merchant Center account to sync your catalog.
                        </p>
                      )}
                    </div>
                  </div>

                  {isConnected && !isPickerOpen && (
                    <Button onClick={() => setIsPickerOpen(true)} className="h-8 px-4 text-xs font-bold">
                      {isGmcLinked ? 'Manage Linked Accounts' : 'Link Accounts'}
                    </Button>
                  )}
                </div>

                {isPickerOpen && (
                  <div className="ml-9 p-4 rounded-xl bg-[#F6F6F7] dark:bg-slate-800 border border-[#E1E3E5] dark:border-slate-700 space-y-5">
                    {/* GMC Account Picker */}
                    <div className="space-y-3">
                      <Label className="text-xs font-bold text-[#202223] dark:text-white">
                        Select Google Merchant Center Account <span className="text-red-500">*</span>
                      </Label>
                      {accounts?.gmcAccounts && accounts.gmcAccounts.length > 0 ? (
                        <div className="space-y-1.5">
                          {accounts.gmcAccounts.map((acc) => (
                            <button
                              key={acc.id}
                              onClick={() => {
                                setSelectedGmcId(acc.id);
                                setSelectedGmcName(acc.name);
                              }}
                              className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                selectedGmcId === acc.id
                                  ? 'border-[#202223] dark:border-white bg-white dark:bg-slate-900 font-bold'
                                  : 'border-[#E1E3E5] dark:border-slate-700 bg-[#F6F6F7] dark:bg-slate-800'
                              }`}
                            >
                              <div>
                                <p className="text-xs font-bold text-[#202223] dark:text-white">{acc.name}</p>
                                <p className="text-[10px] font-mono text-[#5C5F62]">{acc.id}</p>
                              </div>
                              {selectedGmcId === acc.id && (
                                <CheckCircle weight="fill" className="w-4 h-4 text-[#202223] dark:text-white" />
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 rounded-lg border border-dashed border-[#E1E3E5] dark:border-slate-700 bg-white dark:bg-slate-900/50 flex flex-col items-center text-center space-y-3">
                          <p className="text-xs text-[#5C5F62]">
                            No Google Merchant Center accounts found under your profile.
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              onClick={() => window.open('https://merchants.google.com/', '_blank')}
                              className="h-8 px-3 text-xs"
                            >
                              Create Account
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => refetchAccounts()}
                              disabled={isAccountsFetching}
                              className="h-8 px-3 text-xs"
                            >
                              <ArrowsClockwise
                                className={`w-3.5 h-3.5 mr-1.5 ${isAccountsFetching ? 'animate-spin' : ''}`}
                              />
                              Refresh
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ads Account Picker */}
                    <div className="space-y-3 pt-2 border-t border-[#E1E3E5] dark:border-slate-700">
                      <Label className="text-xs font-bold text-[#202223] dark:text-white">
                        Select Google Ads Account (Optional)
                      </Label>
                      {accounts?.adsAccounts && accounts.adsAccounts.length > 0 ? (
                        <div className="space-y-1.5">
                          {accounts.adsAccounts.map((acc) => (
                            <button
                              key={acc.id}
                              onClick={() => {
                                if (selectedAdsId === acc.id) {
                                  setSelectedAdsId('');
                                  setSelectedAdsName('');
                                } else {
                                  setSelectedAdsId(acc.id);
                                  setSelectedAdsName(acc.name);
                                }
                              }}
                              className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-all ${
                                selectedAdsId === acc.id
                                  ? 'border-[#202223] dark:border-white bg-white dark:bg-slate-900 font-bold'
                                  : 'border-[#E1E3E5] dark:border-slate-700 bg-[#F6F6F7] dark:bg-slate-800'
                              }`}
                            >
                              <div>
                                <p className="text-xs font-bold text-[#202223] dark:text-white">{acc.name}</p>
                                <p className="text-[10px] font-mono text-[#5C5F62]">{acc.id}</p>
                              </div>
                              {selectedAdsId === acc.id && (
                                <CheckCircle weight="fill" className="w-4 h-4 text-[#202223] dark:text-white" />
                              )}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 rounded-lg border border-dashed border-[#E1E3E5] dark:border-slate-700 bg-white dark:bg-slate-900/50 flex flex-col items-center text-center space-y-3">
                          <p className="text-xs text-[#5C5F62]">
                            No Google Ads accounts found under your profile.
                          </p>
                          <div className="flex flex-wrap justify-center items-center gap-2">
                            <Button
                              variant="outline"
                              onClick={() => window.open('https://ads.google.com/', '_blank')}
                              className="h-8 px-3 text-xs"
                            >
                              Create Account
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleConnectGoogle}
                              className="h-8 px-3 text-xs"
                            >
                              Reconnect Google
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => refetchAccounts()}
                              disabled={isAccountsFetching}
                              className="h-8 px-3 text-xs"
                            >
                              <ArrowsClockwise
                                className={`w-3.5 h-3.5 mr-1.5 ${isAccountsFetching ? 'animate-spin' : ''}`}
                              />
                              Refresh
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-start gap-2 pt-2 text-[#5C5F62] bg-[#F4F6F8] dark:bg-slate-900/50 p-3 rounded-lg border border-[#E1E3E5] dark:border-slate-700">
                      <Warning className="w-4 h-4 shrink-0 mt-0.5 text-yellow-600" />
                      <div className="text-[11px] leading-tight">
                        <p className="font-bold mb-0.5">Don't forget to link your accounts!</p>
                        <p>
                          Selecting your accounts here allows Dukene to sync your products and track conversions.
                          However, to run Shopping Ads, you must also ensure your Merchant Center is linked to your Ads account inside your Google dashboard.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-[#E1E3E5] dark:border-slate-700">
                      <Button
                        onClick={() => connectMutation.mutate()}
                        disabled={connectMutation.isPending || !selectedGmcId}
                        className="h-8 px-4 text-xs font-bold"
                      >
                        Save Connection
                      </Button>
                      <Button variant="outline" onClick={() => setIsPickerOpen(false)} className="h-8 px-3 text-xs">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 3: Check your online store requirements */}
              <div className="pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full border border-[#D2D5D9] flex items-center justify-center text-xs font-bold text-[#5C5F62] shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-[#202223] dark:text-white">
                      Check your online store requirements
                    </h3>
                  </div>
                </div>

                <div className="ml-9 p-4 rounded-xl bg-[#F6F6F7]/60 dark:bg-slate-800/40 border border-[#E1E3E5] dark:border-slate-800 space-y-3.5">
                  {[
                    {
                      key: 'paymentMethod',
                      title: 'Add a valid payment method',
                      route: MERCHANT_ROUTES.SETTINGS_PAYMENTS,
                      linkText: 'Configure payments →',
                    },
                    {
                      key: 'onlineStore',
                      title: 'Create an online store & remove password',
                      route: MERCHANT_ROUTES.SETTINGS_STORE,
                      linkText: 'Store settings →',
                    },
                    {
                      key: 'refundPolicy',
                      title: 'Add a refund policy and terms of service',
                      route: '/settings',
                      linkText: 'Manage policies →',
                    },
                    {
                      key: 'contactInfo',
                      title: 'Confirm contact info on store',
                      route: MERCHANT_ROUTES.SETTINGS_STORE,
                      linkText: 'Update contact →',
                    },
                  ].map((item) => (
                    <div key={item.key} className="flex items-start justify-between gap-4">
                      <label
                        onClick={() =>
                          setCheckedRequirements((prev) => ({
                            ...prev,
                            [item.key]: !prev[item.key as keyof typeof prev],
                          }))
                        }
                        className="flex items-center gap-3 cursor-pointer group flex-1"
                      >
                        <div className="mt-0.5">
                          {checkedRequirements[item.key as keyof typeof checkedRequirements] ? (
                            <div className="w-4 h-4 rounded-full bg-[#202223] dark:bg-white text-white dark:text-slate-900 flex items-center justify-center">
                              <Check weight="bold" className="w-3 h-3" />
                            </div>
                          ) : (
                            <div className="w-4 h-4 rounded-full border border-[#D2D5D9]" />
                          )}
                        </div>
                        <p className="text-xs font-bold text-[#202223] dark:text-white">{item.title}</p>
                      </label>
                      <button
                        type="button"
                        onClick={() => navigate(item.route)}
                        className="text-xs font-semibold text-[#202223] dark:text-white hover:underline shrink-0"
                      >
                        {item.linkText}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 4: Confirm store setup */}
              <div className="pt-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full border border-[#D2D5D9] flex items-center justify-center text-xs font-bold text-[#5C5F62] shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-[#202223] dark:text-white">Confirm target market & shipping</h3>
                    <p className="text-xs text-[#5C5F62]">
                      Target market: {store?.country || 'Kenya (KE)'} ({store?.currency || 'KES'})
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(MERCHANT_ROUTES.SETTINGS_SHIPPING)}
                  className="text-xs font-semibold text-[#202223] dark:text-white hover:underline"
                >
                  Configure shipping →
                </button>
              </div>

              {/* Step 5: Complete setup */}
              <div className="pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full border border-[#D2D5D9] flex items-center justify-center text-xs font-bold text-[#5C5F62] shrink-0">
                    5
                  </div>
                  <h3 className="text-xs font-bold text-[#202223] dark:text-white">Agree to terms and complete setup</h3>
                </div>

                <div className="ml-9 space-y-3">
                  <label className="flex items-center gap-2 text-xs text-[#5C5F62] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAgreed}
                      onChange={(e) => setTermsAgreed(e.target.checked)}
                      className="rounded border-[#D2D5D9]"
                    />
                    I accept the Google Merchant Center Terms of Service and Dukene channel policies.
                  </label>

                  <Button
                    onClick={() => {
                      saveOnboardingMutation.mutate({
                        googleRequirementsVerified: true,
                        googleTermsAgreed: true,
                        googleSetupCompleted: true,
                        googleSetupCompletedAt: new Date().toISOString(),
                      });
                    }}
                    disabled={!termsAgreed || saveOnboardingMutation.isPending}
                    className="h-8 px-4 text-xs font-bold"
                  >
                    {saveOnboardingMutation.isPending ? 'Completing Setup...' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* ── TAB 4: SETTINGS ──────────────────────────────────────────────── */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 space-y-4 shadow-2xs">
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">Google Channel Settings</h2>

            <div className="space-y-4 max-w-xl">
              <div className="space-y-1.5">
                <Label htmlFor="conversionId" className="text-xs font-bold text-[#202223] dark:text-white">
                  Google Ads Conversion ID (AW-XXXXX)
                </Label>
                <Input
                  id="conversionId"
                  placeholder="e.g. AW-123456789"
                  value={conversionId}
                  onChange={(e) => setConversionId(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="conversionLabel" className="text-xs font-bold text-[#202223] dark:text-white">
                  Conversion Label
                </Label>
                <Input
                  id="conversionLabel"
                  placeholder="e.g. AbC_DeF_GhI_JkL"
                  value={conversionLabel}
                  onChange={(e) => setConversionLabel(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              <div className="space-y-1.5 pt-4 border-t border-[#E1E3E5] dark:border-slate-800">
                <Label htmlFor="siteVerification" className="text-xs font-bold text-[#202223] dark:text-white">
                  Google Site Verification (HTML Tag)
                </Label>
                <Input
                  id="siteVerification"
                  placeholder='e.g. <meta name="google-site-verification" content="XYZ123..." />'
                  value={siteVerification}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Automatically extract the content attribute if they paste the full tag
                    const match = val.match(/content="([^"]+)"/);
                    if (match && match[1]) {
                      setSiteVerification(match[1]);
                    } else {
                      setSiteVerification(val);
                    }
                  }}
                  className="h-8 text-xs"
                />
                <p className="text-[10px] text-[#5C5F62]">
                  Paste the HTML tag provided by Google Merchant Center. We will automatically extract the code.
                </p>
              </div>

              <Button
                onClick={() =>
                  updateSettingsMutation.mutate({
                    googleAdsConversionId: conversionId,
                    googleAdsConversionLabel: conversionLabel,
                    googleSiteVerification: siteVerification,
                  })
                }
                disabled={updateSettingsMutation.isPending}
                className="h-8 px-4 text-xs font-bold rounded-lg"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
